import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { coupleMonogram, coupleShortName, WEDDING_STATUSES, type WeddingStatus } from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import NewWeddingDialog from '@/components/NewWeddingDialog';
import CouplePhoto from '@/components/CouplePhoto';
import { formatDate } from '@/lib/date';

const STATUS_LABELS: Record<WeddingStatus, string> = {
  lead: 'Lead',
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * The portrait tile: a ringed circle, the couple, the date, the venue.
 *
 * The circle is an aspect-square rather than a fixed size so the row reflows
 * with the grid instead of stranding the captions at wide viewports. Everything
 * inside it is centered on the same axis as the text below, which is the whole
 * trick of this layout — the tiles read as a row of portraits, not as cards.
 */
function WeddingTile({
  wedding,
}: {
  wedding: {
    id: string;
    partner1FirstName: string;
    partner2FirstName: string;
    partner1LastName?: string | null;
    partner2LastName?: string | null;
    weddingDate: string | null;
    venueName: string | null;
    photoKey: string | null;
  };
}) {
  return (
    <Link
      to={`/weddings/${wedding.id}`}
      className="group flex flex-col items-center text-center focus-visible:outline-none"
    >
      <div className="w-full max-w-[15rem] transition group-hover:-translate-y-0.5">
        <CouplePhoto
          weddingId={wedding.id}
          hasPhoto={Boolean(wedding.photoKey)}
          monogram={coupleMonogram(wedding)}
          // The caption below names the couple, so an alt repeating it would be
          // read out twice by a screen reader for one link.
          alt=""
          className="transition group-hover:shadow-lg group-focus-visible:ring-2 group-focus-visible:ring-ring"
        />
      </div>

      <p className="label-caps mt-6 text-sm tracking-[0.14em] text-foreground">
        {coupleShortName(wedding)}
      </p>
      <p className="mt-2 font-serif text-sm text-muted-foreground italic">
        {formatDate(wedding.weddingDate, { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
      {wedding.venueName && (
        <p className="mt-2 text-sm text-muted-foreground">{wedding.venueName}</p>
      )}
    </Link>
  );
}

export default function WeddingsPage() {
  const [open, setOpen] = useState(false);

  const { data: weddings, isLoading } = useQuery({
    queryKey: ['weddings'],
    queryFn: async () => {
      const res = await client.api.weddings.$get();
      if (!res.ok) throw new Error('Failed to load weddings');
      return res.json();
    },
  });

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="display-tight text-3xl font-semibold">Weddings</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" />
          New wedding
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : weddings && weddings.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-8 gap-y-14 sm:gap-x-10 lg:grid-cols-3 xl:grid-cols-4">
          {weddings.map((w) => (
            <WeddingTile key={w.id} wedding={w} />
          ))}
        </div>
      ) : (
        <Card className="rounded-3xl bg-card p-10 text-center shadow-ambient">
          <p className="display-tight text-lg font-semibold">No weddings yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a couple and a date. Everything else is optional.
          </p>
          <Button className="mt-5" onClick={() => setOpen(true)}>
            <Plus className="size-4" />
            New wedding
          </Button>
        </Card>
      )}

      <NewWeddingDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

// Referenced above; kept exported so the hub can reuse the same labels.
export { STATUS_LABELS as WEDDING_STATUS_LABELS, WEDDING_STATUSES };
